/**
 * CollapsiblePanel - Reusable collapsible panel wrapper (light theme)
 * Allows panels to be collapsed to save space in the UI
 */

import { useState, useCallback, ReactNode } from 'react';

export interface CollapsiblePanelProps {
  title: string;
  panelId: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

function readPanelState(panelId: string, defaultOpen: boolean): boolean {
  try {
    const stored = localStorage.getItem(`stak-panel:${panelId}`);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    // localStorage unavailable
  }
  return defaultOpen;
}

export function CollapsiblePanel({
  title,
  panelId,
  defaultOpen = false,
  children,
}: CollapsiblePanelProps) {
  const [isOpen, setIsOpen] = useState(() => readPanelState(panelId, defaultOpen));

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(`stak-panel:${panelId}`, next ? '1' : '0');
      } catch {
        // localStorage unavailable
      }
      return next;
    });
  }, [panelId]);

  return (
    <div
      style={{
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        backgroundColor: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Header - always visible */}
      <button
        onClick={toggle}
        style={{
          width: '100%',
          padding: '10px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: isOpen ? '#f8f9fa' : '#fff',
          border: 'none',
          borderBottom: isOpen ? '1px solid #dee2e6' : 'none',
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          fontSize: '13px',
          fontWeight: 600,
          textAlign: 'left',
          color: '#212529',
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = '#fff';
          }
        }}
      >
        <span>{title}</span>
        <span
          style={{
            fontSize: '12px',
            color: '#6c757d',
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block',
          }}
        >
          ▼
        </span>
      </button>

      {/* Content - shown when open */}
      {isOpen && (
        <div
          style={{
            padding: '12px 14px',
            backgroundColor: '#fff',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
